// A new deploy reaches screens that stay open for days (the wall never reloads
// on its own). Every few minutes the page checks which app bundle the site
// now serves; when it differs from the one running, the page reloads itself
// the next time nobody is using it: no touch for a couple of minutes, no sheet
// open, no layout being edited, no text box in use. A phone coming back to the
// app after a while checks straight away and reloads before it is used, so it
// never runs (and crashes on) a version whose files are gone.

const CHECK_MS = 10 * 60_000;
const IDLE_MS = 2 * 60_000;
const RETRY_MS = 30_000;
const BUNDLE = /\/assets\/index-[\w-]+\.js/;

function runningBundle(): string | null {
  for (const s of document.querySelectorAll<HTMLScriptElement>("script[type=module][src]")) {
    const m = BUNDLE.exec(s.src);
    if (m) return m[0];
  }
  return null;
}

async function servedBundle(): Promise<string | null> {
  try {
    const res = await fetch("/", { cache: "no-store", headers: { accept: "text/html" } });
    if (!res.ok) return null;
    return BUNDLE.exec(await res.text())?.[0] ?? null;
  } catch {
    return null;
  }
}

function busy(lastTouch: number): boolean {
  if (Date.now() - lastTouch < IDLE_MS) return true;
  return inUse();
}

/** Something on screen that a reload would lose. */
function inUse(): boolean {
  if (document.querySelector(".sheet, .app-editing, .osk")) return true;
  const el = document.activeElement;
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement;
}

export function installAutoUpdate() {
  // Development builds have no hashed bundle (and Vite reloads them anyway).
  if (!import.meta.env.PROD) return;
  const running = runningBundle();
  if (!running) return;
  let lastTouch = Date.now();
  const touched = () => (lastTouch = Date.now());
  for (const ev of ["pointerdown", "keydown", "wheel"]) document.addEventListener(ev, touched, { capture: true, passive: true });

  let pending = false;
  const reloadWhenQuiet = () => {
    if (busy(lastTouch)) window.setTimeout(reloadWhenQuiet, RETRY_MS);
    else window.location.reload();
  };
  const check = async (resumed = false) => {
    if (pending) return;
    const served = await servedBundle();
    if (served && served !== running) {
      pending = true;
      // Just brought back to the front: nothing is in progress yet, so reload now.
      if (resumed && !inUse()) window.location.reload();
      else reloadWhenQuiet();
    }
  };
  window.setInterval(check, CHECK_MS);
  let hiddenAt = 0;
  const onShow = () => {
    if (document.hidden) {
      hiddenAt = Date.now();
      return;
    }
    if (hiddenAt && Date.now() - hiddenAt > RETRY_MS) void check(true);
  };
  document.addEventListener("visibilitychange", onShow);
  // A page restored from the browser's back-forward cache is an old page too.
  window.addEventListener("pageshow", (e) => e.persisted && void check(true));
}
